import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  FormGroup,
  FormHelperText,
  Tooltip,
  IconButton,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useStore } from "@xyflow/react";

const filenamePlaceholders = [
  { value: "{date}", label: "Current Date" },
  { value: "{time}", label: "Current Time" },
  { value: "{datetime}", label: "Date and Time" },
  { value: "{nodeid}", label: "Node ID" },
  { value: "{reportname}", label: "Report Name" },
];

const exportFormats = [
  { value: "docx", label: "Word Document (.docx)" },
  { value: "pdf", label: "PDF (.pdf)" },
];

const documentTemplates = [
  { value: "simple", label: "Simple Table" },
  { value: "formatted", label: "Formatted Report" },
  { value: "summary", label: "Executive Summary" },
  { value: "detailed", label: "Detailed Analysis" },
];

export const WordExportNodeModal = ({ open, node, selectedInputId: parentSelectedInputId }) => {
  // Error handling for null/undefined node
  if (!node) {
    console.error("WordExportNodeModal: Received null or undefined node");
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Error: Unable to load node data</Typography>
      </Box>
    );
  }

  const nodeData = node?.data || {};

  // Multi-input state management
  const [upstreamSources, setUpstreamSources] = useState([]);
  const selectedInputId = parentSelectedInputId; // Use parent's selectedInputId
  const [fieldsPerInput, setFieldsPerInput] = useState({});
  const [exportFormat, setExportFormat] = useState("docx");
  const [documentTemplate, setDocumentTemplate] = useState("formatted");
  const [downloadAsFile, setDownloadAsFile] = useState(true);
  const [filenameTemplate, setFilenameTemplate] = useState("report-{datetime}");
  const [includeHeaders, setIncludeHeaders] = useState(true);
  const [includeCharts, setIncludeCharts] = useState(false);
  const [includeSummary, setIncludeSummary] = useState(true);
  const [pageOrientation, setPageOrientation] = useState("portrait");
  const [fontSize, setFontSize] = useState("11");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [error, setError] = useState(null);

  // Simple refs for initialization tracking
  const initializedRef = useRef(false);
  const lastInitializedNodeId = useRef(null);
  const autoSelectedFieldsRef = useRef(false);
  const lastPersistedSettingsRef = useRef(null);

  // Get store data with error handling
  const nodes = useStore((s) => {
    try {
      return s.nodes || [];
    } catch (error) {
      console.error("Error accessing nodes from store:", error);
      return [];
    }
  });

  const edges = useStore((s) => {
    try {
      return s.edges || [];
    } catch (error) {
      console.error("Error accessing edges from store:", error);
      return [];
    }
  });

  // Get all upstream data sources
  useEffect(() => {
    if (!open || !node?.id) return;

    try {
      // Find all edges where this node is the target
      const incomingEdges = edges.filter((e) => e.target === node?.id);

      if (incomingEdges.length === 0) {
        setUpstreamSources([]);
        return;
      }

      const sources = incomingEdges
        .map((edge) => {
          const sourceNode = nodes.find((n) => n.id === edge.source);
          if (!sourceNode) return null;

          let foundData = null;
          let displayName =
            sourceNode.data?.name || sourceNode.data?.label || `Node ${sourceNode.id}`;

          console.log("WordExport: Processing source node", sourceNode.id, {
            type: sourceNode.data?.type || sourceNode.type,
            hasOutput: !!sourceNode.data?.output,
            hasSelectedSchema: !!sourceNode.data?.output?.selectedSchema,
          });

          // Extract data based on node type
          if (sourceNode.data?.type === "data.model" || sourceNode.type === "data.model") {
            // Check execution results first (most up-to-date)
            if (sourceNode.data?.executionState?.preview?.results) {
              foundData = { results: sourceNode.data.executionState.preview.results };
            }
            // Check for selectedSchema in output (this is how model nodes store their data)
            else if (sourceNode.data?.output?.selectedSchema) {
              foundData = { selectedSchema: sourceNode.data.output.selectedSchema };
            }
            // Check output schema (fallback)
            else if (sourceNode.data?.output?.schema) {
              foundData = { schema: sourceNode.data.output.schema };
            }
            // Then check model schema
            else if (sourceNode.data?.model?.schema) {
              foundData = { schema: sourceNode.data.model.schema };
            }
            // Check if there's raw data
            else if (sourceNode.data?.data && Array.isArray(sourceNode.data.data)) {
              foundData = { data: sourceNode.data.data };
            }
          } else if (sourceNode.data?.type === "data.filter" || sourceNode.type === "data.filter") {
            if (sourceNode.data?.executionState?.preview?.results) {
              foundData = { results: sourceNode.data.executionState.preview.results };
            } else if (sourceNode.data?.output?.results) {
              foundData = { results: sourceNode.data.output.results };
            } else if (sourceNode.data?.output?.settings) {
              foundData = { settings: sourceNode.data.output.settings };
            }
          }

          if (!foundData) {
            console.warn("WordExport: No data found for source node", sourceNode.id);
            return null;
          }

          return {
            nodeId: sourceNode.id,
            name: displayName,
            type: sourceNode.data?.type || sourceNode.type,
            data: foundData,
            handleId: edge.targetHandle,
          };
        })
        .filter(Boolean);

      console.log("WordExport: Found upstream sources:", sources);
      setUpstreamSources(sources);
    } catch (error) {
      console.error("WordExport: Error processing upstream sources:", error);
      setError("Failed to load upstream data sources");
    }
  }, [open, node?.id, nodes, edges]);

  // Extract fields from data sources
  const extractFieldsFromSource = useCallback((source) => {
    if (!source?.data) return [];

    try {
      let fields = [];

      if (source.data.results && Array.isArray(source.data.results)) {
        if (source.data.results.length > 0) {
          const firstRow = source.data.results[0];
          fields = Object.keys(firstRow).map((key) => ({
            key,
            type: typeof firstRow[key],
            label: key,
          }));
        }
      } else if (source.data.selectedSchema?.properties) {
        fields = Object.entries(source.data.selectedSchema.properties).map(([key, prop]) => ({
          key,
          type: prop.type || "string",
          label: prop.title || key,
        }));
      } else if (source.data.schema?.properties) {
        fields = Object.entries(source.data.schema.properties).map(([key, prop]) => ({
          key,
          type: prop.type || "string",
          label: prop.title || key,
        }));
      } else if (source.data.settings?.fieldsToInclude) {
        fields = source.data.settings.fieldsToInclude.map((field) => ({
          key: field,
          type: "string",
          label: field,
        }));
      } else if (
        source.data.data &&
        Array.isArray(source.data.data) &&
        source.data.data.length > 0
      ) {
        const firstRow = source.data.data[0];
        fields = Object.keys(firstRow).map((key) => ({
          key,
          type: typeof firstRow[key],
          label: key,
        }));
      }

      console.log("WordExport: Extracted fields for source", source.nodeId, ":", fields);
      return fields;
    } catch (error) {
      console.error("WordExport: Error extracting fields from source:", error);
      return [];
    }
  }, []);

  // Initialize fields when sources change
  useEffect(() => {
    if (upstreamSources.length === 0) {
      setFieldsPerInput({});
      return;
    }

    const newFieldsPerInput = {};
    let autoSelectedAny = false;

    upstreamSources.forEach((source) => {
      const fields = extractFieldsFromSource(source);
      const inputKey = source.handleId || source.nodeId;

      // Check if we need to initialize this input
      if (!fieldsPerInput[inputKey] && fields.length > 0) {
        // Default to "ALL" instead of individual fields
        newFieldsPerInput[inputKey] = {
          availableFields: fields,
          selectedFields: ["ALL"],
        };
        autoSelectedAny = true;
      } else {
        // Keep existing selection but update available fields
        newFieldsPerInput[inputKey] = {
          availableFields: fields,
          selectedFields: fieldsPerInput[inputKey]?.selectedFields || [],
        };
      }
    });

    if (autoSelectedAny || Object.keys(fieldsPerInput).length === 0) {
      setFieldsPerInput(newFieldsPerInput);
      autoSelectedFieldsRef.current = true;
    }
  }, [upstreamSources, extractFieldsFromSource]);

  // Persist settings to node output
  useEffect(() => {
    if (!node?.data?.setData) return;

    const settings = {
      exportFormat,
      documentTemplate,
      downloadAsFile,
      filenameTemplate,
      includeHeaders,
      includeCharts,
      includeSummary,
      pageOrientation,
      fontSize,
      fieldsPerInput,
      upstreamSources: upstreamSources.map((s) => ({
        nodeId: s.nodeId,
        name: s.name,
        type: s.type,
        handleId: s.handleId,
      })),
    };

    // Only persist if settings have actually changed
    const settingsStr = JSON.stringify(settings);
    if (settingsStr !== lastPersistedSettingsRef.current) {
      console.log("WordExport: Persisting settings", settings);

      node.data.setData((oldData) => ({
        ...oldData,
        output: {
          ...oldData.output,
          settings,
        },
      }));

      lastPersistedSettingsRef.current = settingsStr;
    }
  }, [
    exportFormat,
    documentTemplate,
    downloadAsFile,
    filenameTemplate,
    includeHeaders,
    includeCharts,
    includeSummary,
    pageOrientation,
    fontSize,
    fieldsPerInput,
    upstreamSources,
    node?.data?.setData,
  ]);

  const handleFieldSelectionChange = (inputKey, newValue) => {
    if (!inputKey) return;

    // Extract field keys from the new value
    let newFields = newValue
      .map((v) => {
        // Handle both object format { key: "fieldName", label: "Label" } and string format
        if (typeof v === "object" && v !== null) {
          return v.key || v.label || v;
        }
        return v;
      })
      .filter(Boolean);

    // Handle "ALL" selection logic
    const ALL_OPTION = "ALL";
    const currentFields = fieldsPerInput[inputKey]?.selectedFields || [];

    if (newFields.includes(ALL_OPTION)) {
      // If "ALL" was just added (wasn't in previous selection)
      if (!currentFields.includes(ALL_OPTION)) {
        newFields = [ALL_OPTION]; // Only "ALL"
      } else {
        // If "ALL" was already selected and other fields are being added, remove "ALL"
        newFields = newFields.filter((f) => f !== ALL_OPTION);
      }
    }

    setFieldsPerInput((prev) => ({
      ...prev,
      [inputKey]: {
        ...prev[inputKey],
        selectedFields: newFields,
      },
    }));
  };

  // Render field selection for an input
  const renderFieldSelection = (source) => {
    const inputKey = source.handleId || source.nodeId;
    const inputData = fieldsPerInput[inputKey];

    if (!inputData || !inputData.availableFields?.length) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No fields available from this source
        </Typography>
      );
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="subtitle2">Select Fields from {source.name}:</Typography>
          <Typography variant="caption" color="text.secondary">
            {inputData.selectedFields?.includes("ALL")
              ? `All ${inputData.availableFields.length} fields selected`
              : `${inputData.selectedFields?.length || 0}/${inputData.availableFields.length} fields selected`}
          </Typography>
        </Box>
        <Autocomplete
          multiple
          options={[
            { key: "ALL", label: "🔘 All Fields", type: "special" },
            ...inputData.availableFields,
          ]}
          getOptionLabel={(option) => {
            if (option.key === "ALL") return "🔘 All Fields";
            return option.label || option.key || option;
          }}
          isOptionEqualToValue={(option, value) => {
            // Handle comparison between objects and strings
            const optionKey = typeof option === "object" ? option.key : option;
            const valueKey = typeof value === "object" ? value.key : value;
            return optionKey === valueKey;
          }}
          value={
            inputData.selectedFields?.map((field) => {
              if (field === "ALL") return { key: "ALL", label: "🔘 All Fields", type: "special" };
              const found = inputData.availableFields.find((f) => f.key === field);
              return found || { key: field, label: field, type: "string" };
            }) || []
          }
          onChange={(event, newValue) => {
            handleFieldSelectionChange(inputKey, newValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              placeholder={`Choose fields to include in document (${inputData.availableFields.length} available)`}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#2A2A2A",
                  color: "#CCC",
                },
                "& .MuiInputBase-input": {
                  padding: 0,
                  margin: 0,
                  fontSize: "12px",
                  lineHeight: "20px",
                },
                "& .MuiChip-root": {
                  height: "24px",
                  fontSize: "12px",
                  margin: "2px",
                },
              }}
            />
          )}
          sx={{
            minWidth: 250,
            "& .MuiAutocomplete-inputRoot": {
              padding: 0,
            },
            "& .MuiAutocomplete-tag": {
              margin: "2px",
            },
          }}
          disableCloseOnSelect
        />
        <FormHelperText>
          {inputData.selectedFields?.includes("ALL")
            ? `All ${inputData.availableFields.length} fields will be included in the document`
            : `${inputData.selectedFields?.length || 0} of ${inputData.availableFields.length} fields selected`}
        </FormHelperText>
      </Box>
    );
  };

  return (
    <Box sx={{ marginTop: "20px" }}>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {upstreamSources.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          ⚠ No data sources connected. Connect this node to a data provider to configure export
          settings.
        </Typography>
      ) : (
        <>
          {/* Export Format */}
          <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
            <InputLabel>Export Format</InputLabel>
            <Select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
              label="Export Format"
              sx={{
                backgroundColor: "#2A2A2A",
                color: "#CCC",
              }}
            >
              {exportFormats.map((format) => (
                <MenuItem key={format.value} value={format.value}>
                  {format.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Document Template */}
          <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
            <InputLabel>Document Template</InputLabel>
            <Select
              value={documentTemplate}
              onChange={(e) => setDocumentTemplate(e.target.value)}
              label="Document Template"
              sx={{
                backgroundColor: "#2A2A2A",
                color: "#CCC",
              }}
            >
              {documentTemplates.map((template) => (
                <MenuItem key={template.value} value={template.value}>
                  {template.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Filename Template */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Filename Template
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            value={filenameTemplate}
            onChange={(e) => setFilenameTemplate(e.target.value)}
            placeholder="report-{datetime}"
            sx={{
              mb: 2,
              backgroundColor: "#2A2A2A",
              "& .MuiOutlinedInput-root": {
                color: "#CCC",
              },
            }}
          />
          <FormHelperText sx={{ mb: 3, color: "#999" }}>
            Use placeholders: {"{date}"}, {"{time}"}, {"{datetime}"}, {"{nodeid}"}, {"{reportname}"}
            . Extension will be added automatically.
          </FormHelperText>

          {/* Document Options */}
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Document Options
          </Typography>

          <FormGroup sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={includeHeaders}
                  onChange={(e) => setIncludeHeaders(e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#FF6C58",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#FF6C58",
                    },
                  }}
                />
              }
              label="Include Table Headers"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={includeSummary}
                  onChange={(e) => setIncludeSummary(e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#FF6C58",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#FF6C58",
                    },
                  }}
                />
              }
              label="Include Summary Section"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={includeCharts}
                  onChange={(e) => setIncludeCharts(e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#FF6C58",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#FF6C58",
                    },
                  }}
                />
              }
              label="Include Charts (if available)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={downloadAsFile}
                  onChange={(e) => setDownloadAsFile(e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#FF6C58",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#FF6C58",
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography>Download as File</Typography>
                  <Tooltip title="When enabled, generates a downloadable file. When disabled, returns file content as a base64 string in the node output.">
                    <IconButton size="small" sx={{ ml: 0.5 }}>
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            />
          </FormGroup>
          <FormHelperText sx={{ mb: 3, color: "#999" }}>
            {downloadAsFile
              ? "File will be available for download when the node executes"
              : "File content will be passed as output to the next node"}
          </FormHelperText>

          {/* Page Settings */}
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Page Settings
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <FormControl sx={{ minWidth: 140 }}>
              <InputLabel>Orientation</InputLabel>
              <Select
                value={pageOrientation}
                onChange={(e) => setPageOrientation(e.target.value)}
                label="Orientation"
                sx={{
                  backgroundColor: "#2A2A2A",
                  color: "#CCC",
                }}
              >
                <MenuItem value="portrait">Portrait</MenuItem>
                <MenuItem value="landscape">Landscape</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 100 }}>
              <InputLabel>Font Size</InputLabel>
              <Select
                value={fontSize}
                onChange={(e) => setFontSize(e.target.value)}
                label="Font Size"
                sx={{
                  backgroundColor: "#2A2A2A",
                  color: "#CCC",
                }}
              >
                <MenuItem value="9">9pt</MenuItem>
                <MenuItem value="10">10pt</MenuItem>
                <MenuItem value="11">11pt</MenuItem>
                <MenuItem value="12">12pt</MenuItem>
                <MenuItem value="14">14pt</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Field Selection */}
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Data to Include
          </Typography>

          {upstreamSources.map((source) => (
            <Box key={source.nodeId} sx={{ mb: 2 }}>
              {renderFieldSelection(source)}
            </Box>
          ))}
        </>
      )}
    </Box>
  );
};
